// Hợp đồng API của màn «Quản lý GPM» + cầu nối chạy trên máy cài GPM Login.
import { z } from 'zod';
/**
 * QUẢN LÝ GPM (11/10/2026, chủ dự án: «thiết kế 1 chức năng quản lý GPM, liên kết để tôi điều khiển trên web» → điều
 * khiển TỪ XA, xem / bật / tắt profile, nối profile với mail, tạo / sửa / xoá profile).
 *
 * GPM Login chỉ mở API trên chính máy cài (http://localhost:9495/api/v1, không ra Internet). Nên trên máy ấy chạy một
 * «cầu nối» (file .cmd tải từ web, PowerShell có sẵn của Windows): tự kết nối RA máy chủ công ty bằng mã khoá riêng của
 * máy, hỏi lệnh (chờ tối đa ~20 giây mỗi lượt), gọi API của GPM, gửi kết quả + ảnh chụp danh sách profile về. Web chỉ
 * xếp lệnh vào hàng đợi và đọc ảnh chụp — điện thoại hay máy khác đều điều khiển được.
 */
export const GPM_NAME_MAX = 80;
/** Máy có lượt hỏi lệnh trong ngần này giây thì coi là đang kết nối. */
export const GPM_ONLINE_SECONDS = 60;
/** Lệnh chưa được cầu nối nhận / làm xong trong ngần này giây thì báo hỏng. */
export const GPM_COMMAND_TIMEOUT_SECONDS = 120;
/** Riêng lệnh «uploadvideo» chạy lâu (tải CẢ LOẠT video trong thư mục) — hạn rất dài (4 giờ). */
export const GPM_UPLOAD_TIMEOUT_SECONDS = 14400;
export const GPM_ACTIONS = [
    'start',
    'stop',
    'create',
    'update',
    'delete',
    'refresh',
    'login',
    'grab2fa',
    'uploadvideo',
    'pickfolder',
    /**
     * TẢI VIDEO (24/10/2026 Dựng lại video → 28/10/2026 chỉ còn tải, xem `video-download.ts`): máy chủ TỰ xếp hai lệnh này (web không gửi thẳng) — `remake`
     * giao một việc cho cầu nối (cầu nối mở thợ riêng rồi báo xong ngay), `remakecancel` bảo diệt thợ của việc.
     */
    'remake',
    'remakecancel',
];
/**
 * TRÌNH DUYỆT HALO (13/10/2026, chủ dự án: «thiết kế hệ thống trình duyệt tôi đăng nhập kênh trên đó, add proxy, bạn
 * điều khiển và liên kết dễ dàng»): cầu nối tự mở Chrome / Edge THẬT có sẵn trên máy, mỗi profile một thư mục dữ liệu
 * riêng (`E:\HALO-Browser\<mã>\data`), proxy theo profile (có mật khẩu thì qua bộ chuyển tiếp cục bộ), điều khiển
 * qua CDP y như profile GPM. ĐĂNG NHẬP LÀM TAY (không có lệnh «login»), không giả vân tay. Chạy SONG SONG với GPM
 * trên cùng máy: mã profile HALO mang tiền tố `halo-` (GPM cấp GUID) — mọi nơi nhận ra loại qua tiền tố, không
 * thêm cột.
 */
export const HALO_PROFILE_PREFIX = 'halo-';
/** Nhóm của profile HALO: cầu nối đặt mã «halo:<tên nhóm>» (nhóm GPM là mã GPM cấp). */
export const HALO_GROUP_PREFIX = 'halo:';
export const GPM_ENGINES = ['gpm', 'halo'];
export function profileEngine(profileId) {
    return profileId.startsWith(HALO_PROFILE_PREFIX) ? 'halo' : 'gpm';
}
/** Cầu nối từ bản này mới có Trình duyệt HALO — máy chạy bản cũ phải tải lại file cầu nối. */
export const GPM_BRIDGE_HALO_VERSION = 5;
const profileId = z.string().trim().min(1).max(100);
const text = (max) => z.string().trim().max(max);
export const gpmProfileFieldsSchema = z.object({
    name: text(GPM_NAME_MAX).min(1),
    /** Mã nhóm của GPM; '' = nhóm mặc định. */
    groupId: text(100).optional(),
    /** Tên nhóm muốn gán: cầu nối tìm nhóm theo tên, chưa có thì TẠO nhóm mới rồi gán. Ưu tiên hơn `groupId`. */
    groupName: text(200).optional(),
    /** Chuỗi proxy GPM nhận (vd. socks5://ip:port, http://user:pass@ip:port); '' = không proxy. */
    rawProxy: text(500).optional(),
    note: text(1000).optional(),
    /** Trang mở sẵn khi bật profile, mỗi dòng một địa chỉ. */
    startupUrls: text(2000).optional(),
    /** Chỉ lúc TẠO: 'halo' = Trình duyệt HALO (Chrome / Edge thật trên máy), mặc định 'gpm' = profile GPM Login. */
    engine: z.enum(GPM_ENGINES).optional(),
});
export const gpmCommandSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('start'), profileId }),
    z.object({ action: z.literal('stop'), profileId }),
    /** Xoá đưa vào thùng rác của GPM (khôi phục được trong app GPM) — không xoá hẳn. */
    z.object({ action: z.literal('delete'), profileId }),
    z.object({ action: z.literal('refresh') }),
    z.object({ action: z.literal('create'), fields: gpmProfileFieldsSchema }),
    z.object({
        action: z.literal('update'),
        profileId,
        fields: gpmProfileFieldsSchema.partial(),
    }),
    /**
     * ĐĂNG NHẬP MAIL (12/10/2026, chủ dự án «tự đăng nhập trọn gói»): web CHỈ gửi mã dòng mail
     * (`recordId` ở Hệ thống mail), KHÔNG gửi mật khẩu. Máy chủ tra mail / mật khẩu / mã 2FA ngay lúc cầu
     * nối hỏi lệnh rồi mới kèm vào lệnh gửi xuống (xem `GpmLoginBridgeCommand`), nên mật khẩu không bao giờ
     * nằm trong hàng đợi lệnh hay nhật ký.
     */
    z.object({ action: z.literal('login'), profileId, recordId: z.number().int().positive() }),
    /**
     * LẤY MÃ BÍ MẬT 2FA (12/10/2026, chủ dự án «tự lập Authenticator + lấy khoá»): với một profile ĐANG
     * ĐĂNG NHẬP SẴN, cầu nối vào phần Bảo mật của tài khoản Google, lập một ứng dụng xác thực MỚI, lấy khoá
     * bí mật base32 rồi gửi về máy chủ lưu vào ô 2FA của dòng mail (`recordId`). KHÔNG gửi mật khẩu từ web —
     * máy chủ tra lúc cầu nối hỏi lệnh (mật khẩu dùng cho bước Google bắt xác thực lại).
     */
    z.object({ action: z.literal('grab2fa'), profileId, recordId: z.number().int().positive() }),
    /**
     * TẢI VIDEO LÊN (12/10/2026, chủ dự án «liên kết upload video» → qua trình duyệt GPM): với profile ĐANG
     * ĐĂNG NHẬP SẴN, cầu nối chuyển sang kênh phụ (theo tên) rồi tải video trong YouTube Studio từ THƯ MỤC trên
     * máy (`folderPath`: chứa x.mp4 + «x - seo.txt» + «x - thumb.jpg»); tiêu đề/mô tả/tag đọc từ seo.txt, đăng
     * Không công khai. `folderPath` là đường dẫn cục bộ người nhập (không phải bí mật).
     */
    z.object({
        action: z.literal('uploadvideo'),
        profileId,
        recordId: z.number().int().positive(),
        folderPath: text(500).min(1),
    }),
    /**
     * CHỌN THƯ MỤC (12/10/2026): cầu nối mở hộp chọn thư mục của Windows NGAY trên máy cài GPM rồi trả về
     * đường dẫn tuyệt đối (trình duyệt web không đọc được đường dẫn thật). Không cần profile.
     */
    z.object({ action: z.literal('pickfolder') }),
]);
export const gpmCommandRequestSchema = z.object({
    agentId: z.number().int().positive(),
    command: gpmCommandSchema,
});
export const gpmAgentCreateSchema = z.object({ name: text(GPM_NAME_MAX).min(1) });
export const gpmAgentParamsSchema = z.object({ id: z.coerce.number().int().positive() });
export const gpmCommandParamsSchema = z.object({ id: z.coerce.number().int().positive() });
export const gpmProfilesQuerySchema = z.object({
    agentId: z.coerce.number().int().positive().optional(),
});
// ---------------------------------------------------------------- phía cầu nối (mã khoá, không phiên đăng nhập)
export const gpmAgentPollSchema = z.object({
    version: text(40).default(''),
    machine: text(120).default(''),
    gpmReady: z.boolean().default(false),
    gpmVersion: text(80).default(''),
    error: text(500).default(''),
    /** Trình duyệt HALO (cầu nối bản 5+): có Chrome / Edge trên máy không, và tên + bản + thư mục profile. */
    browserReady: z.boolean().default(false),
    browserInfo: text(200).default(''),
});
const agentProfileSchema = z.object({
    id: profileId,
    name: text(200).default(''),
    groupId: text(100).default(''),
    rawProxy: text(500).default(''),
    browser: text(80).default(''),
    note: text(2000).default(''),
    running: z.boolean().default(false),
    createdAt: text(40).default(''),
});
export const gpmAgentSyncSchema = z.object({
    groups: z
        .array(z.object({ id: text(100).min(1), name: text(200).default('') }))
        .max(5000)
        .default([]),
    profiles: z.array(agentProfileSchema).max(20000),
    /**
     * Ảnh chụp có phần GPM không (cầu nối bản 5+). GPM Login đang tắt thì cầu nối chỉ gửi profile Trình duyệt HALO
     * (`false`) → máy chủ GIỮ profile + nhóm GPM đã có, chỉ thay phần HALO. Cầu nối cũ không gửi → `true` = thay cả bộ.
     */
    gpmIncluded: z.boolean().default(true),
});
export const gpmAgentResultSchema = z.object({
    commandId: z.number().int().positive(),
    ok: z.boolean(),
    error: text(1000).default(''),
    /** Với lệnh «create»: mã profile GPM vừa tạo trả về, để web nối với mail. */
    profileId: text(100).default(''),
    /** Với lệnh «grab2fa»: khoá bí mật 2FA base32 vừa lập, máy chủ lưu vào ô 2FA của dòng mail. */
    otpSecret: text(200).default(''),
});
//# sourceMappingURL=gpm.js.map